import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { fetchAllPages } from "../../utils/exportHelpers";
import { Box, Paper, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CalendarDate } from "@internationalized/date";
import { fetchPublisherTagging, fetchPublisherTaggingFilterOptions, searchGroupPublishers, bulkUpdatePublisherTagging, setPlatform, setClientIds, setTag, setGroupPublisherName, setPublisherLink, setDateRange, setPage, setSortConfig, selectPublisherTaggingRows, selectPublisherTaggingPagination, selectPublisherTaggingLoading, selectPublisherTaggingFilters, selectFilterOptions, selectGroupPublisherSearch, selectGroupPublisherSearchLoading, selectPublisherTaggingUpdating, selectPublisherTaggingLastSyncedAt, selectPublisherTaggingRefreshLoading, refreshPublisherTagging, setSearchQuery, } from "../../redux/publishertaggingslice";
// Components
import PageContainer from "../../common_components/PageContainer";
import PagesCommonHeader from "../../common_components/PagesCommonHeader";
import LastUpdatedInfo from "../../common_components/LastUpdatedInfo";
import Pagination from "../../common_components/Pagination";
import CommonTable from "../../common_components/CommonTable";
import { Dropdown, ExportButton } from "../../common_components/FormInputs";
import TableToolbar from "../../common_components/TableToolbar";
import CustomDatePicker from "../../common_components/daterangepicker/CustomDatePicker";
import { PlatformBadge } from "./components/PlatformBadge";
import { ClientChips } from "./components/ClientChips";
import { InlineTagDropdown } from "./components/InlineTagDropdown";
import { InlineUrlEdit } from "./components/InlineUrlEdit";
import { InlineG8aEdit } from "./components/InlineG8aEdit";
import { FloatingSaveBar } from "../../common_components/FloatingSaveBar";
import { ExpandedContent } from "./components/ExpandedContent";
import { TagConflictModal } from "./components/TagConflictModal";
import { useLayout } from "../../contexts/LayoutContext";
// Utils
import { truncateName } from "../../helper/truncateName";
import { EXPAND_TRIGGERS } from "../../common_components/tableConstants";
import { TAG_FILTER_OPTIONS, PUBLISHER_LINK_OPTIONS, tableContainerSx } from "./utils/constants";
import { NETWORK_LABEL_MAP } from "../../utils/networkConstants";
import { fmt, fmtDate } from "./utils/helpers";
import exportPublisherTagging from "./ExportPublisherTagging";
export default function PublisherTaggingPage() {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const initialized = useRef(false);
    const rows = useSelector(selectPublisherTaggingRows);
    const pagination = useSelector(selectPublisherTaggingPagination);
    const loading = useSelector(selectPublisherTaggingLoading);
    const filters = useSelector(selectPublisherTaggingFilters);
    const filterOptions = useSelector(selectFilterOptions);
    const groupPublisherSearch = useSelector(selectGroupPublisherSearch);
    const groupPublisherSearchLoading = useSelector(selectGroupPublisherSearchLoading);
    const updating = useSelector(selectPublisherTaggingUpdating);
    const lastSyncedAt = useSelector(selectPublisherTaggingLastSyncedAt);
    const refreshLoading = useSelector(selectPublisherTaggingRefreshLoading);
    const permissions = useSelector((state) => state.auth.permissions);
    const canManageTagging = useMemo(() => permissions?.["publisher-tagging"]?.actions?.includes("Manage Tagging"), [permissions]);
    const canManageUrls = useMemo(() => permissions?.["publisher-tagging"]?.actions?.includes("Manage Publisher URLs"), [permissions]);
    const [pendingChanges, setPendingChanges] = useState({});
    const [exporting, setExporting] = useState(false);
    const changeCount = Object.keys(pendingChanges).length;
    const { platform, clientIds, tag, groupPublisherName, publisherLink, startDate, endDate, page, limit, sortBy, sortOrder, q } = filters;
    const [tableSearch, setTableSearch] = useState(q);
    const [sortConfig, setSortConfigLocal] = useState({ columnId: sortBy || "revenue", direction: sortOrder || "desc" });
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    const [columnVisibilityAnchor, setColumnVisibilityAnchor] = useState(null);
    const [columnFilters, setColumnFilters] = useState({});
    const [columnOrder, setColumnOrder] = useState(null);
    const [showFilterRow, setShowFilterRow] = useState(false);
    const [activeFilterColumnId, setActiveFilterColumnId] = useState(null);
    const [pinnedColumns, setPinnedColumns] = useState({ left: [], right: [] });
    const [controlledExpandedRows, setControlledExpandedRows] = useState(new Set());
    const [saveBarMessage, setSaveBarMessage] = useState("Unsaved edits in this page");
    const [conflictModalOpen, setConflictModalOpen] = useState(false);
    const [conflictData, setConflictData] = useState(null);
    const pendingConflictResolution = useRef(null);
    const promptTagConflict = useCallback((data) => {
        return new Promise((resolve, reject) => {
            setConflictData(data);
            setConflictModalOpen(true);
            pendingConflictResolution.current = { resolve, reject };
        });
    }, []);
    const handleConflictModalClose = useCallback(() => {
        setConflictModalOpen(false);
        if (pendingConflictResolution.current) {
            pendingConflictResolution.current.reject();
            pendingConflictResolution.current = null;
        }
    }, []);
    const handleConflictModalSelect = useCallback((tag) => {
        setConflictModalOpen(false);
        if (pendingConflictResolution.current) {
            pendingConflictResolution.current.resolve(tag);
            pendingConflictResolution.current = null;
        }
    }, []);
    const [recentGroupAssignments, setRecentGroupAssignments] = useState({});
    const isFirstLoad = useRef(true);
    const { setIsSidebarExpanded } = useLayout();
    const dateValue = useMemo(() => {
        if (!startDate || !endDate)
            return null;
        const [sy, sm, sd] = startDate.split("-").map(Number);
        const [ey, em, ed] = endDate.split("-").map(Number);
        return { start: new CalendarDate(sy, sm, sd), end: new CalendarDate(ey, em, ed) };
    }, [startDate, endDate]);
    const [isReady, setIsReady] = useState(false);
    const dayCount = useMemo(() => {
        if (!startDate || !endDate)
            return 90;
        return Math.round((new Date(endDate).getTime() - new Date(startDate).getTime()) / 86400000);
    }, [startDate, endDate]);
    const platformOptions = useMemo(() => [
        { value: "All", label: "All Platforms" },
        ...(filterOptions?.platforms || ["awin", "impact", "avantlink", "cj", "pepperjam", "rakuten", "levanta", "costingtons", "commission_factory"])
            .map(p => ({ value: p, label: NETWORK_LABEL_MAP[p] || (p.charAt(0).toUpperCase() + p.slice(1)) })),
    ], [filterOptions]);
    const clientOptions = useMemo(() => [
        { value: "", label: "All Clients" },
        ...(filterOptions?.clients || []).map(c => ({ value: c._id, label: c.name })),
    ], [filterOptions]);
    const groupPublisherOptions = useMemo(() => [
        { value: "", label: "All Groups" },
        ...(filterOptions?.group_publisher_names || []).map(n => ({ value: n, label: n })),
    ], [filterOptions]);
    const doFetch = useCallback(() => {
        dispatch(fetchPublisherTagging({
            platform,
            client_ids: clientIds.length ? clientIds : undefined,
            tag: tag || undefined,
            group_publisher_name: groupPublisherName || undefined,
            publisher_link: publisherLink || undefined,
            startDate, endDate, page, limit, sortBy, sortOrder,
            q: q || undefined,
        }));
    }, [dispatch, platform, clientIds, tag, groupPublisherName, publisherLink, startDate, endDate, page, limit, sortBy, sortOrder, q]);
    useEffect(() => {
        setIsSidebarExpanded(false);
    }, [setIsSidebarExpanded]);
    useEffect(() => {
        if (initialized.current)
            return;
        initialized.current = true;
        // Sync URL query params to Redux filters on first load
        const urlTag = searchParams.get("tag");
        const urlLink = searchParams.get("publisherLink");
        const urlGroup = searchParams.get("group");
        if (urlTag)
            dispatch(setTag(urlTag));
        if (urlLink !== null)
            dispatch(setPublisherLink(urlLink));
        if (urlGroup)
            dispatch(setGroupPublisherName(urlGroup));
        dispatch(fetchPublisherTaggingFilterOptions({ platform: "All" }));
        dispatch(searchGroupPublishers({}));
        // Mark as ready so the first doFetch can proceed
        setIsReady(true);
    }, [dispatch, searchParams]);
    useEffect(() => { dispatch(fetchPublisherTaggingFilterOptions({ platform })); }, [dispatch, platform]);
    useEffect(() => {
        if (isReady) {
            doFetch();
        }
    }, [doFetch, isReady]);
    const handleDateApply = useCallback(({ mainRange }) => {
        if (!mainRange)
            return;
        dispatch(setDateRange({
            startDate: `${mainRange.start.year}-${String(mainRange.start.month).padStart(2, "0")}-${String(mainRange.start.day).padStart(2, "0")}`,
            endDate: `${mainRange.end.year}-${String(mainRange.end.month).padStart(2, "0")}-${String(mainRange.end.day).padStart(2, "0")}`,
        }));
    }, [dispatch]);
    const handleSortChange = useCallback((columnId, direction) => {
        setSortConfigLocal({ columnId, direction });
        dispatch(setSortConfig({ sortBy: columnId || "date", sortOrder: direction }));
    }, [dispatch]);
    const handleSaveTag = useCallback(async (row, newTag) => {
        const docIds = row.summary.publisher_doc_ids || [];
        const pendingGroupKey = `group:${docIds.join(",")}`;
        const g8aId = pendingChanges[pendingGroupKey]?.payload?.g8aPublisherId ||
            row.summary.g8aPublisherId;
        const key = `tag:${docIds[0]}`;
        // ✅ Check if there's a group tag that will override this individual tag
        let groupTag = null;
        let groupName = "";
        if (g8aId) {
            // This row is part of a group - check for group-level tag
            const normalizedG8aId = String(g8aId).trim();
            // 1. Check pending changes first
            const groupTagKey = `tag:${normalizedG8aId}`;
            if (pendingChanges[groupTagKey]) {
                groupTag = pendingChanges[groupTagKey].payload.tag;
            }
            else {
                // 2. Check all rows in the table to find the group row
                const groupRow = rows.find(r => (r.summary.g8aPublisherId && String(r.summary.g8aPublisherId).trim() === normalizedG8aId) ||
                    (r.details.some(d => d.g8aPublisherId && String(d.g8aPublisherId).trim() === normalizedG8aId)));
                if (groupRow) {
                    groupTag = groupRow.summary.tag || groupRow.details.find(d => d.tag)?.tag || null;
                    groupName = groupRow.summary.g8aPublisherName || row.summary.g8aPublisherName || "the group";
                }
            }
        }
        const recentAssignment = recentGroupAssignments[docIds[0]];
        const shouldShowOverrideWarning = !!recentAssignment &&
            !!groupTag &&
            groupTag !== newTag;
        if (shouldShowOverrideWarning) {
            setSaveBarMessage(`⚠️ ${recentAssignment.groupName} already has tag "${groupTag}". "${newTag || "No Tag"}" will be overridden.`);
        }
        else {
            setSaveBarMessage("Unsaved edits in this page");
        }
        setPendingChanges(prev => ({
            ...prev,
            [key]: {
                type: "tag",
                payload: {
                    g8aPublisherId: g8aId || undefined,
                    // Ungrouped rows can now represent one publisher merged across
                    // multiple clients (same publisher_id, different client_id) —
                    // send every doc so the tag reaches all of them, not just one.
                    publisher_doc_ids: g8aId ? undefined : docIds,
                    tag: newTag // ✅ Store the user-selected tag, backend will apply group override
                },
                displayValue: newTag || "No Tag",
                label: groupTag
                    ? `${row.summary.publisherName || "Publisher"}: Tag → ${newTag || "No Tag"} (group "${groupName}" overrides with ${groupTag})`
                    : `${row.summary.publisherName || "Publisher"}: Tag → ${newTag || "No Tag"}`
            }
        }));
    }, [rows, pendingChanges, recentGroupAssignments]);
    const handleSaveG8a = useCallback(async (row, g8aPublisherId, g8aPublisherName) => {
        const docIds = row.summary.publisher_doc_ids || row.details.map((d) => d.id);
        const publisherDocIdForTag = docIds[0];
        const currentRowG8aId = row.summary.g8aPublisherId;
        // Immediate logic for Tag Inheritance
        let targetGroupTag = null;
        let matchedGroupName = g8aPublisherName || "";
        if (g8aPublisherId) {
            const normalizedTargetId = String(g8aPublisherId).trim();
            // 1. Check pending changes first
            const targetGroupTagKey = `tag:${normalizedTargetId}`;
            if (pendingChanges[targetGroupTagKey]) {
                targetGroupTag = pendingChanges[targetGroupTagKey].payload.tag;
            }
            else {
                // 2. Check all rows in the table
                const existingGroupRow = rows.find(r => (r.summary.g8aPublisherId && String(r.summary.g8aPublisherId).trim() === normalizedTargetId) ||
                    (r.details.some(d => d.g8aPublisherId && String(d.g8aPublisherId).trim() === normalizedTargetId)));
                if (existingGroupRow) {
                    targetGroupTag = existingGroupRow.summary.tag || existingGroupRow.details.find(d => d.tag)?.tag || null;
                    matchedGroupName = existingGroupRow.summary.g8aPublisherName || g8aPublisherName || "";
                }
            }
        }
        const currentRowTagKey = `tag:${currentRowG8aId || publisherDocIdForTag}`;
        const currentRowTag = pendingChanges[currentRowTagKey] ? pendingChanges[currentRowTagKey].payload.tag : row.summary.tag;
        let finalTagToApply = null;
        let didResolveConflict = false;
        if (g8aPublisherId && targetGroupTag) {
            if (currentRowTag && currentRowTag !== targetGroupTag) {
                try {
                    const chosenTag = await promptTagConflict({
                        publisherName: row.summary.publisherName,
                        groupName: matchedGroupName,
                        publisherTag: currentRowTag,
                        groupTag: targetGroupTag
                    });
                    finalTagToApply = chosenTag;
                    didResolveConflict = true;
                    setSaveBarMessage(`Note: You chose to apply tag "${finalTagToApply || "None"}" to the group ${matchedGroupName}`);
                }
                catch (e) {
                    // User cancelled the prompt
                    return;
                }
            }
            else {
                finalTagToApply = targetGroupTag;
            }
        }
        else if (g8aPublisherId && currentRowTag) {
            finalTagToApply = currentRowTag;
            setSaveBarMessage(`Note: Group "${matchedGroupName}" will inherit tag "${currentRowTag}" from ${row.summary.publisherName}`);
        }
        if (g8aPublisherId) {
            setRecentGroupAssignments(prev => ({
                ...prev,
                [publisherDocIdForTag]: {
                    groupId: String(g8aPublisherId).trim(),
                    groupName: matchedGroupName,
                    groupTag: targetGroupTag || null,
                }
            }));
        }
        setPendingChanges(prev => {
            const next = { ...prev };
            const groupKey = `group:${docIds.join(",")}`;
            next[groupKey] = {
                type: "group",
                payload: { publisher_doc_ids: docIds, g8aPublisherId, g8aPublisherName: g8aPublisherName || undefined },
                displayValue: g8aPublisherName || "No Group",
                label: `${row.summary.publisherName || "Publisher"}: Group → ${g8aPublisherName || "No Group"}`
            };
            if (g8aPublisherId) {
                if (finalTagToApply !== null || didResolveConflict) {
                    const tagKey = `tag:${String(g8aPublisherId).trim()}`;
                    next[tagKey] = {
                        type: "tag",
                        payload: { g8aPublisherId, tag: finalTagToApply },
                        displayValue: finalTagToApply || "No Tag",
                        label: `${matchedGroupName || g8aPublisherName || "G8A Group"}: Tag → ${finalTagToApply || "No Tag"} (auto-updated for group)`
                    };
                }
            }
            return next;
        });
    }, [rows, pendingChanges, setRecentGroupAssignments]);
    const handleSaveDetailG8a = useCallback(async (detail, g8aPublisherId, g8aPublisherName) => {
        const publisherDocIdForTag = detail.id;
        const currentRowG8aId = detail.g8aPublisherId;
        let targetGroupTag = null;
        if (g8aPublisherId) {
            const normalizedTargetId = String(g8aPublisherId).trim();
            const targetGroupTagKey = `tag:${normalizedTargetId}`;
            if (pendingChanges[targetGroupTagKey]) {
                targetGroupTag = pendingChanges[targetGroupTagKey].payload.tag;
            }
            else {
                const existingGroupRow = rows.find(r => (r.summary.g8aPublisherId && String(r.summary.g8aPublisherId).trim() === normalizedTargetId) ||
                    (r.details.some(d => d.g8aPublisherId && String(d.g8aPublisherId).trim() === normalizedTargetId)));
                if (existingGroupRow) {
                    targetGroupTag = existingGroupRow.summary.tag || existingGroupRow.details.find(d => d.tag)?.tag || null;
                }
            }
        }
        const currentRowTagKey = `tag:${currentRowG8aId || publisherDocIdForTag}`;
        const currentRowTag = pendingChanges[currentRowTagKey] ? pendingChanges[currentRowTagKey].payload.tag : detail.tag;
        let finalTagToApply = null;
        let didResolveConflict = false;
        let matchedGroupName = g8aPublisherName || "";
        if (g8aPublisherId && targetGroupTag) {
            if (currentRowTag && currentRowTag !== targetGroupTag) {
                try {
                    const chosenTag = await promptTagConflict({
                        publisherName: detail.publisherName,
                        groupName: matchedGroupName,
                        publisherTag: currentRowTag,
                        groupTag: targetGroupTag
                    });
                    finalTagToApply = chosenTag;
                    didResolveConflict = true;
                    setSaveBarMessage(`Note: You chose to apply tag "${finalTagToApply || "None"}" to the group ${matchedGroupName}`);
                }
                catch (e) {
                    // User cancelled the prompt
                    return;
                }
            }
            else {
                finalTagToApply = targetGroupTag;
            }
        }
        else if (g8aPublisherId && currentRowTag) {
            finalTagToApply = currentRowTag;
            setSaveBarMessage(`Note: Group "${matchedGroupName}" will inherit tag "${currentRowTag}" from ${detail.publisherName}`);
        }
        setPendingChanges(prev => {
            const next = { ...prev };
            const groupKey = `group:${detail.id}`;
            next[groupKey] = {
                type: "group",
                payload: { publisher_doc_ids: [detail.id], g8aPublisherId, g8aPublisherName: g8aPublisherName || undefined },
                displayValue: g8aPublisherName || "No Group",
                label: `${detail.publisherName || "Publisher"}: Group → ${g8aPublisherName || "No Group"}`
            };
            if (g8aPublisherId) {
                if (finalTagToApply !== null || didResolveConflict) {
                    const tagKey = `tag:${String(g8aPublisherId).trim()}`;
                    next[tagKey] = {
                        type: "tag",
                        payload: { g8aPublisherId, tag: finalTagToApply },
                        displayValue: finalTagToApply || "No Tag",
                        label: `${matchedGroupName || g8aPublisherName || "G8A Group"}: Tag → ${finalTagToApply || "No Tag"} (auto-updated for group)`
                    };
                }
            }
            return next;
        });
    }, [rows, pendingChanges]);
    const handleUpdateUrl = useCallback(async (docId, newUrl, publisherName) => {
        const key = `url:${docId}`;
        setPendingChanges(prev => ({
            ...prev,
            [key]: {
                type: "url",
                payload: { publisher_doc_id: docId, publisher_url: newUrl },
                displayValue: newUrl || "No URL",
                label: `${publisherName}: URL → ${newUrl || "No URL"}`
            }
        }));
    }, []);
    const handleBulkSave = async () => {
        const updates = Object.values(pendingChanges).map(c => ({ type: c.type, payload: c.payload }));
        const res = await dispatch(bulkUpdatePublisherTagging({ updates }));
        if (bulkUpdatePublisherTagging.fulfilled.match(res)) {
            setPendingChanges({});
            doFetch();
            dispatch(fetchPublisherTaggingFilterOptions({ platform }));
            dispatch(searchGroupPublishers({}));
        }
    };
    const handleResetChanges = () => { setPendingChanges({}); setSaveBarMessage("Unsaved edits in this page"); };
    const handleGroupSearch = useCallback((q) => { dispatch(searchGroupPublishers({ q })); }, [dispatch]);
    const handleExport = useCallback(async () => {
        if (exporting)
            return;
        setExporting(true);
        try {
            // Fetch all pages in safe batches instead of one massive request
            const allRows = await fetchAllPages(async (page, limit) => {
                const result = await dispatch(fetchPublisherTagging({
                    platform,
                    client_ids: clientIds.length ? clientIds : undefined,
                    tag: tag || undefined,
                    group_publisher_name: groupPublisherName || undefined,
                    publisher_link: publisherLink || undefined,
                    startDate, endDate,
                    page, limit,
                    sortBy, sortOrder,
                })).unwrap();
                return {
                    items: result.rows || [],
                    totalPages: result.pagination?.total_pages || 1,
                };
            });
            exportPublisherTagging(allRows, startDate, endDate);
        }
        catch (error) {
            console.error('Export failed:', error);
        }
        finally {
            setExporting(false);
        }
    }, [dispatch, exporting, platform, clientIds, tag, groupPublisherName, publisherLink, startDate, endDate, sortBy, sortOrder]);
    const handleRefresh = useCallback(async () => {
        if (!platform || platform === "All" || refreshLoading)
            return;
        const clientId = clientIds.length === 1 ? clientIds[0] : undefined;
        await dispatch(refreshPublisherTagging({ platform, startDate, endDate, clientId })).unwrap();
        doFetch();
    }, [dispatch, platform, startDate, endDate, refreshLoading, doFetch, clientIds]);
    useEffect(() => {
        const timer = setTimeout(() => {
            dispatch(setSearchQuery(tableSearch));
        }, 500);
        return () => clearTimeout(timer);
    }, [tableSearch, dispatch]);
    const changeDetails = useMemo(() => Object.values(pendingChanges).map(c => c.label), [pendingChanges]);
    const handleFilterRowToggle = useCallback((payload = null) => {
        if (!payload || payload?.mode === "all") {
            setActiveFilterColumnId(null);
            setShowFilterRow(prev => payload?.open !== undefined ? payload.open : !prev);
            return;
        }
        if (payload?.mode === "single" && payload?.columnId) {
            if (payload?.open === false) {
                setActiveFilterColumnId(null);
                setShowFilterRow(false);
                return;
            }
            setActiveFilterColumnId(payload.columnId);
            setShowFilterRow(true);
        }
    }, []);
    const enrichedRows = useMemo(() => {
        return rows.map((row, i) => {
            const summaryClients = (row.summary.client || "").split(",").map(c => c.trim()).filter(Boolean);
            const detailClients = (row.details || []).map(d => d.client?.trim()).filter(Boolean);
            const allClients = Array.from(new Set([...summaryClients, ...detailClients]));
            return {
                ...row,
                id: row.summary.g8aPublisherId || `row_${i}`,
                summary: { ...row.summary, client: allClients.join(", ") },
            };
        });
    }, [rows]);
    const handleExpandToggle = useCallback((rowId) => {
        setControlledExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(rowId)) {
                next.delete(rowId);
            }
            else {
                next.add(rowId);
            }
            return next;
        });
    }, []);
    const handleExpandAllToggle = useCallback((expand) => {
        if (expand) {
            const allIds = enrichedRows.filter(row => row.details && row.details.length > 0).map(row => row.id);
            setControlledExpandedRows(new Set(allIds));
        }
        else {
            setControlledExpandedRows(new Set());
        }
    }, [enrichedRows]);
    const columns = [
        { id: "date", label: "Date", width: "100px", align: "left", sortable: true },
        { id: "client", label: "Client", width: "120px", align: "left", sortable: true },
        { id: "g8aPublisherName", label: "G8A Publisher Name", width: "150px", align: "left", sortable: false },
        { id: "g8aPublisherId", label: "G8A Publisher ID", width: "130px", align: "left", sortable: false },
        { id: "tag", label: "Tag", width: "120px", align: "left", sortable: true },
        { id: "platform", label: "Platform", width: "80px", align: "left", sortable: true },
        { id: "revenue", label: "Revenue", width: "100px", align: "left", sortable: true },
        { id: "clicks", label: "Clicks", width: "80px", align: "left", sortable: true },
        { id: "orders", label: "Orders", width: "80px", align: "left", sortable: true },
    ];
    const [detailSort, setDetailSort] = useState({ column: "", direction: "asc" });
    const handleDetailSort = useCallback((col) => {
        setDetailSort(prev => ({
            column: col,
            direction: prev.column === col && prev.direction === "asc" ? "desc" : "asc"
        }));
    }, []);
    const renderCell = useCallback((row, column) => {
        const s = row.summary;
        const docIds = s.publisher_doc_ids || [];
        const g8aId = s.g8aPublisherId;
        const tagKey = `tag:${docIds[0]}`;
        const groupKey = `group:${docIds.join(",")}`;
        const urlKey = `url:${docIds[0]}`;
        const currentTag = pendingChanges[tagKey] ? pendingChanges[tagKey].payload.tag : s.tag;
        const currentG8aName = pendingChanges[groupKey] ? pendingChanges[groupKey].displayValue : s.g8aPublisherName;
        const currentG8aId = pendingChanges[groupKey] ? pendingChanges[groupKey].payload.g8aPublisherId : s.g8aPublisherId;
        const currentUrl = pendingChanges[urlKey] ? pendingChanges[urlKey].displayValue : s.publisherUrl;
        switch (column.id) {
            case "date": return _jsx(Typography, { className: "all-Font-Sizes", sx: { color: "#101828" }, children: fmtDate(s.date) });
            case "publisherName": return _jsx(InlineUrlEdit, { publisherName: s.publisherName, publisherUrl: currentUrl, onSave: (url) => handleUpdateUrl(docIds[0], url, s.publisherName), saving: updating, isDirty: !!pendingChanges[urlKey], disabled: !canManageUrls });
            case "client": return _jsx(ClientChips, { clients: s.client, maxWidth: 100 });
            case "g8aPublisherName": return _jsx(InlineG8aEdit, { g8aPublisherName: currentG8aName, g8aPublisherId: currentG8aId, publisherDocIds: docIds, publisherName: s.publisherName, publisherId: s.publisherId, onSave: (id, name) => handleSaveG8a(row, id, name), saving: updating, searchResults: groupPublisherSearch, searchLoading: groupPublisherSearchLoading, onSearch: handleGroupSearch, editField: "name", isDirty: !!pendingChanges[groupKey], disabled: !canManageTagging });
            case "g8aPublisherId": return _jsx(InlineG8aEdit, { g8aPublisherName: currentG8aName, g8aPublisherId: currentG8aId, publisherDocIds: docIds, publisherName: s.publisherName, publisherId: s.publisherId, onSave: (id, name) => handleSaveG8a(row, id, name), saving: updating, searchResults: groupPublisherSearch, searchLoading: groupPublisherSearchLoading, onSearch: handleGroupSearch, editField: "id", hideEditIcon: true, isDirty: !!pendingChanges[groupKey], disabled: !canManageTagging });
            case "tag": return _jsx(InlineTagDropdown, { tag: currentTag, onSave: (t) => handleSaveTag(row, t), saving: updating, isDirty: !!pendingChanges[tagKey], disabled: !canManageTagging });
            case "platform": return _jsx(PlatformBadge, { platform: s.platform });
            case "revenue": return _jsxs(Typography, { className: "all-Font-Sizes", sx: { color: "#101828" }, children: [_jsx("span", { style: { color: "#a1a1aa" }, children: "$ " }), fmt(s.revenue)] });
            case "clicks": return _jsx(Typography, { className: "all-Font-Sizes", sx: { color: "#101828" }, children: fmt(s.clicks) });
            case "orders": return _jsx(Typography, { className: "all-Font-Sizes", sx: { color: "#101828" }, children: fmt(s.orders) });
            default: return null;
        }
    }, [updating, groupPublisherSearch, groupPublisherSearchLoading, handleUpdateUrl, handleSaveG8a, handleSaveTag, handleGroupSearch, pendingChanges]);
    const renderExpandedContent = useCallback((row) => (_jsx(ExpandedContent, { row: row, detailSort: detailSort, updating: updating, groupPublisherSearch: groupPublisherSearch, groupPublisherSearchLoading: groupPublisherSearchLoading, handleUpdateUrl: handleUpdateUrl, handleSaveDetailG8a: handleSaveDetailG8a, handleGroupSearch: handleGroupSearch, handleDetailSort: handleDetailSort, canManageTagging: canManageTagging, canManageUrls: canManageUrls })), [detailSort, updating, groupPublisherSearch, groupPublisherSearchLoading, handleUpdateUrl, handleSaveDetailG8a, handleGroupSearch, handleDetailSort]);
    const renderDropdownValue = (label, value) => (_jsxs("span", { className: "all-Font-Sizes", children: [_jsxs("span", { style: { color: "#101828", fontWeight: 500 }, children: [label, ":"] }), " ", _jsx("span", { style: { color: "#667085" }, children: value })] }));
    const selectedClient = clientOptions.find(c => clientIds.includes(c.value));
    const selectedTagOpt = TAG_FILTER_OPTIONS.find(t => t.value === tag);
    const selectedGroup = groupPublisherOptions.find(g => g.value === groupPublisherName);
    const selectedLink = PUBLISHER_LINK_OPTIONS.find(l => l.value === publisherLink);
    return (_jsxs(PageContainer, { children: [_jsxs(Paper, { elevation: 0, sx: { display: "flex", flexDirection: "column", gap: "8px" }, children: [_jsx(PagesCommonHeader, { height: "auto", lastUpdatedSection: _jsx(LastUpdatedInfo, { updatedAt: lastSyncedAt, onRefresh: handleRefresh, loading: refreshLoading, moduleName: "publisher-tagging", disabled: platform === "All", disabledReason: platform === "All" ? "Please select a platform to refresh" : "" }), rightSection: _jsxs(Box, { sx: { display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end", width: "100%", }, children: [_jsx(Dropdown, { value: platform, onChange: (v) => dispatch(setPlatform(v)), options: platformOptions, displayValue: renderDropdownValue("Platform", platform === "All" ? "All" : truncateName(platformOptions.find(p => p.value === platform)?.label || "All", 10)), leftIcon: "/assets/widgetadd.svg", size: "sm", fullWidth: false, className: "header-dropdown" }), _jsx(Dropdown, { value: clientIds[0] || "", onChange: (v) => dispatch(setClientIds(v ? [v] : [])), options: clientOptions, displayValue: renderDropdownValue("Client", truncateName(selectedClient?.label || "All", 10)), leftIcon: "/assets/userC.svg", size: "sm", fullWidth: false, className: "header-dropdown", searchable: clientOptions.length > 10 }), _jsx(Dropdown, { value: tag, onChange: (v) => dispatch(setTag(v)), options: TAG_FILTER_OPTIONS, displayValue: renderDropdownValue("Funnel Tag", selectedTagOpt?.label || "All"), leftIcon: "/assets/LeadAmC.svg", size: "sm", fullWidth: false, className: "header-dropdown", sortByLabel: false }), _jsx(Dropdown, { value: groupPublisherName, onChange: (v) => dispatch(setGroupPublisherName(v)), options: groupPublisherOptions, displayValue: renderDropdownValue("Group", groupPublisherName ? truncateName(selectedGroup?.label || "All", 10) : "All"), leftIcon: "/assets/TypeC.svg", size: "sm", fullWidth: false, className: "header-dropdown", searchable: true, sortByLabel: false }), _jsx(Dropdown, { value: publisherLink, onChange: (v) => dispatch(setPublisherLink(v)), options: PUBLISHER_LINK_OPTIONS, displayValue: renderDropdownValue("Publisher Link", selectedLink?.label || "All"), leftIcon: "/assets/link.svg", size: "sm", fullWidth: false, className: "header-dropdown", sortByLabel: false })] }) }), _jsx(PagesCommonHeader, { height: "auto", lastUpdatedSection: _jsx("div", {}), rightSection: _jsxs(Box, { sx: { display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end", width: "100%" }, className: "header-padding-remove", children: [_jsx(TableToolbar, { title: "", showSearch: true, showRows: true, showExpand: true, showFullScreen: true, onSearchChange: (v) => setTableSearch(v), searchValue: tableSearch, enableColumnVisibility: true, onColumnVisibilityClick: (e) => setColumnVisibilityAnchor(e.currentTarget), enableColumnFilters: false, showFilterRow: showFilterRow, onFilterRowToggle: handleFilterRowToggle, searchPlaceholder: "Search by Publisher Name" }), _jsx(CustomDatePicker, { value: dateValue, onApply: handleDateApply, comparison: false }), _jsx(ExportButton, { loading: exporting, onClick: handleExport })] }) }), _jsxs(Box, { className: "publisher-tagging-table", sx: { ...tableContainerSx, position: "relative", flex: 1, display: "flex", flexDirection: "column" }, children: [_jsx(CommonTable, { columns: columns, rows: enrichedRows, renderCell: renderCell, sortConfig: sortConfig, onSortChange: handleSortChange, expandTrigger: EXPAND_TRIGGERS.ICON, renderExpandedContent: renderExpandedContent, isLoading: loading || refreshLoading, isFirstLoad: isFirstLoad.current, enableSelection: false, selectedRows: selectedRows, onSelectionChange: setSelectedRows, onExpandToggle: handleExpandToggle, controlledExpandedRows: controlledExpandedRows, isRowExpandable: (row) => row.details && row.details.length > 0, onExpandAllToggle: handleExpandAllToggle, columnVisibilityConfig: { enable: true, columns: columns.map(c => c.id), enableReorder: true }, hiddenColumns: hiddenColumns, onColumnVisibilityChange: setHiddenColumns, columnVisibilityAnchorEl: columnVisibilityAnchor, onColumnVisibilityClose: () => setColumnVisibilityAnchor(null), enableColumnActions: true, enableColumnFilters: false, showFilterRow: showFilterRow, activeFilterColumnId: activeFilterColumnId, onActiveFilterColumnIdChange: setActiveFilterColumnId, columnFilters: columnFilters, onColumnFilterChange: (colId, val) => setColumnFilters(prev => { const next = { ...prev }; if (val)
                                    next[colId] = val;
                                else
                                    delete next[colId]; return next; }), onFilterRowToggle: handleFilterRowToggle, enableColumnReorder: true, columnOrder: columnOrder, onColumnOrderChange: setColumnOrder, enableColumnPinning: true, pinnedColumns: pinnedColumns, onColumnPinningChange: setPinnedColumns, tableHeadClassName: "reusable-table-header-label all-Font-Sizes", stickyHeader: true, maxHeight: "calc(100vh - 280px)" }), pagination && pagination.total_groups > 0 && !loading && !refreshLoading && (_jsx(Box, { className: "user-list-pagination-wrapper", children: _jsx(Pagination, { currentPage: pagination.page, totalPages: pagination.total_pages, onPageChange: (p) => dispatch(setPage(p)), maxVisiblePages: 5, totalEntries: pagination.total_groups, itemsPerPage: pagination.limit, showEntriesMessage: true }) }))] }), _jsx(FloatingSaveBar, { count: changeCount, onReset: handleResetChanges, onSave: handleBulkSave, updating: updating, isVisible: changeCount > 0, message: saveBarMessage, variant: saveBarMessage.includes("⚠️") || saveBarMessage.startsWith("Note:") ? "warning" : "default", countLabel: "Changes Pending", details: changeDetails })] }), _jsx(TagConflictModal, { open: conflictModalOpen, data: conflictData, onClose: handleConflictModalClose, onSelectTag: handleConflictModalSelect })] }));
}
